import { useState, useEffect, useRef } from "react";
import { SetupState } from "./types";
import { themePresets } from "./data/themes";
import { authWithGoogleCredential } from "./lib/apiClient";
import {
  isLocalDevHost,
  deriveLocalDevTenantId,
  activateLocalDevSessionBridge,
  mockLocalCreateTenantResponse,
  mockLocalUpdateSettingsResponse,
  mockLocalLogoUploadResponse,
  mockLocalCompleteOnboardingResponse,
} from "./lib/localDevSessionBridge";
import {
  writeAppSession,
  readSessionToken,
  readSessionTenantId,
  readOwnerFromStorage,
  hasActiveSession,
  clearAppSession,
  writeSessionTenantId,
  writeSessionTenantActive,
} from "./lib/appSession";
import { Step1Form } from "./components/Step1Form";
import { Step2Form } from "./components/Step2Form";
import { SummaryStep } from "./components/SummaryStep";
import { SuccessPopup } from "./components/SuccessPopup";
import { DormDashboard } from "./components/DormDashboard";
import { BillPreview } from "./components/BillPreview";
import {
  Building2,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  HelpCircle,
  Check,
  BookOpen,
  ShieldCheck,
  Compass,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";

const initialSetupState: SetupState = {
  // Step 1: ข้อมูลหอพัก
  dormName: "",
  province: "",
  dormType: "mix",
  rooms: [],
  roomInputMethod: "auto",
  manualRoomText: "",
  buildingsList: [],

  // Step 2: ออกแบบบิลหอพัก
  themeId: "emerald",
  logoUrl: "",
  logoScale: 1.0,
  logoPositionX: 0,
  logoPositionY: 0,
  elecRate: 7,
  elecRateType: "per_unit",
  waterRateType: "per_room",
  waterRatePrice: "",
  hasCommonFee: false,
  commonFeePrice: "",
  commonFeeType: "per_room",
  hasWifiFee: false,
  wifiFeePrice: "",
  wifiFeeType: "per_room",
  meterRolloverLimit: 10000,
  promptPayNumber: "",
  accountName: "",
  promoCode: "",
  agreeTerms: false,
  step3Consent: false,
  address: "",
  googleMapUrl: "",
};

export default function App() {
  const [state, setState] = useState<SetupState>(initialSetupState);
  const [currentStep, setCurrentStep] = useState<number>(1); // 1, 2, 3 (Summary)
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isWizardCompleted, setIsWizardCompleted] = useState<boolean>(
    () => localStorage.getItem("worker_tenant_status") === "active",
  );
  const [showSuccessPopup, setShowSuccessPopup] = useState<boolean>(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState<boolean>(false);
  const [bootstrapResolved, setBootstrapResolved] =
    useState<boolean>(!hasActiveSession());
  const [routeDecisionReady, setRouteDecisionReady] =
    useState<boolean>(!hasActiveSession());
  const [isBackendUnavailable, setIsBackendUnavailable] =
    useState<boolean>(false);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  // Google Auth State
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(hasActiveSession);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [currentUser, setCurrentUser] = useState<any>(readOwnerFromStorage);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => setCurrentPath(window.location.pathname);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const navigateTo = (path: string) => {
    if (window.location.pathname !== path) {
      window.history.replaceState(null, "", path);
      setCurrentPath(path);
    }
  };

  // Route enforcing effect
  useEffect(() => {
    // ROUTE GUARD CONTRACT FIX
    const hasActiveWorkerTenant =
      localStorage.getItem("worker_tenant_status") === "active";
    const sessionHasTenant =
      sessionStorage.getItem("wiz_has_tenant") === "true";

    const backendTenantExists = sessionHasTenant || hasActiveWorkerTenant;

    // We still need to let the app know if there's a draft to prefill, but NOT to route to dashboard
    // const hasSafeSetupEvidence = isLoggedIn && state.dormName && state.dormName !== 'ข้อมูลหอพักของคุณ';

    const isSetupComplete = isWizardCompleted || backendTenantExists;

    let path = window.location.pathname;
    if (path.length > 1 && path.endsWith("/")) {
      path = path.slice(0, -1);
    }

    const allowedRoutes = [
      "/",
      "/index.html",
      "/login",
      "/login.html",
      "/setup",
      "/setup.html",
      "/dashboard",
      "/dashboard.html",
      "/admin-v2",
      "/tenant-v2",
    ];

    // Do not redirect to dashboard if routeDecisionReady is false, unless we already know tenant exists locally
    const canDecide = routeDecisionReady || backendTenantExists;

    if (path === "/" || path === "" || path === "/index.html") {
      if (!isLoggedIn) navigateTo("/login");
      else if (backendTenantExists) navigateTo("/dashboard");
      else if (canDecide) navigateTo("/setup");
    } else if (path === "/login" || path === "/login.html") {
      if (isLoggedIn) {
        if (backendTenantExists) navigateTo("/dashboard");
        else if (canDecide) navigateTo("/setup");
      }
    } else if (path === "/setup" || path === "/setup.html") {
      if (!isLoggedIn) navigateTo("/login");
      else if (backendTenantExists) navigateTo("/dashboard");
    } else if (path === "/dashboard" || path === "/dashboard.html") {
      if (!isLoggedIn) navigateTo("/login");
      else if (!backendTenantExists && !isBackendUnavailable && canDecide)
        navigateTo("/setup");
    } else if (!allowedRoutes.includes(path)) {
      if (!isLoggedIn) navigateTo("/login");
      else if (backendTenantExists) navigateTo("/dashboard");
      else if (!isBackendUnavailable && canDecide) navigateTo("/setup");
    }
  }, [
    isLoggedIn,
    isWizardCompleted,
    currentPath,
    routeDecisionReady,
    isBackendUnavailable,
  ]);

  // Interactive Design Direction Style Choice
  // 'neo-saas' (Modern professional slate) vs 'boutique-editorial' (Warm cozy cream)
  const designVibe = "boutique-editorial";

  const [isGoogleScriptLoaded, setIsGoogleScriptLoaded] =
    useState<boolean>(false);
  const [googleLoadError, setGoogleLoadError] = useState<boolean>(false);

  useEffect(() => {
    const isLoginPage =
      currentPath === "/login" || currentPath === "/login.html";

    if (!isLoggedIn && isLoginPage) {
      const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
      if (!clientId) {
        console.warn("VITE_GOOGLE_CLIENT_ID is not defined.");
      }

      let checkInterval: number;
      let timeoutId: number;

      const initGoogleAuth = () => {
        if ((window as any).google?.accounts?.id?.initialize && clientId) {
          setIsGoogleScriptLoaded(true);

          try {
            (window as any).google.accounts.id.initialize({
              client_id: clientId,
              callback: handleCredentialResponse,
            });

            const btn = document.getElementById("google-signin-button");
            if (btn) {
              btn.innerHTML = ""; // Clear existing contents for fresh render
              (window as any).google.accounts.id.renderButton(btn, {
                theme: "outline",
                size: "large",
                type: "standard",
                text: "signin_with",
              });
            } else {
              console.warn(
                "Failed to find google-signin-button to render Google UI.",
              );
            }
          } catch (e) {
            console.warn("Google init warning:", e);
          }
        }
      };

      if ((window as any).google?.accounts?.id?.initialize) {
        initGoogleAuth();
      } else {
        checkInterval = window.setInterval(() => {
          if ((window as any).google?.accounts?.id?.initialize) {
            clearInterval(checkInterval);
            clearTimeout(timeoutId);
            initGoogleAuth();
          }
        }, 100);

        timeoutId = window.setTimeout(() => {
          clearInterval(checkInterval);
          if (!(window as any).google?.accounts?.id?.initialize) {
            setGoogleLoadError(true);
          }
        }, 5000);
      }

      return () => {
        if (checkInterval) clearInterval(checkInterval);
        if (timeoutId) clearTimeout(timeoutId);
      };
    }
  }, [isLoggedIn, currentPath]);

  // Canonical Bootstrap Effect
  useEffect(() => {
    let isCancelled = false;

    const runCanonicalBootstrap = async () => {
      const sessionToken = readSessionToken();
      let tenantId = readSessionTenantId();
      let hasTenant =
        !!tenantId ||
        sessionStorage.getItem("wiz_has_tenant") === "true" ||
        localStorage.getItem("worker_has_tenant") === "true";
      let ownerObj = readOwnerFromStorage();
      let ownerKey = ownerObj ? ownerObj.id || ownerObj.email : null;

      let normalizedDormName = "";
      let isComplete =
        localStorage.getItem("worker_tenant_status") === "active";

      if (isLoggedIn) {
        if (sessionToken && !tenantId) {
          try {
            const { getSession } = await import("./lib/apiClient");
            const sessionRes = await getSession(sessionToken);
            if (sessionRes.ok && sessionRes.session) {
              tenantId =
                sessionRes.tenantId || sessionRes.session.tenantId || null;
              hasTenant =
                sessionRes.hasTenant || sessionRes.session.hasTenant || false;
              ownerObj = ownerObj || {
                id: sessionRes.session.ownerId,
                email: sessionRes.session.email,
                displayName: "",
              };
              ownerKey = ownerObj.id || ownerObj.email;

              const { writeAppSession } = await import("./lib/appSession");
              writeAppSession({
                sessionToken,
                tenantId,
                hasTenant,
                owner: ownerObj as any,
              });
            }
          } catch (e: any) {
            console.warn("Session lookup failed during bootstrap", e);
            if (e.code === "tenant_lookup_unavailable" || e.status === 503) {
              if (!isCancelled) setIsBackendUnavailable(true);
            } else if (
              e.status === 401 ||
              e.code === "UNAUTHORIZED" ||
              e.message === "Expired"
            ) {
              if (!isCancelled) handleLogout();
            }
          }
        }

        if (sessionToken && tenantId && hasTenant) {
          setIsLoadingProfile(true);
          try {
            const { getTenantProfile } = await import("./lib/apiClient");
            const profile = await getTenantProfile(sessionToken, tenantId);
            if (profile.ok && profile.tenant) {
              if (profile.tenant.status === "active") {
                isComplete = true;
                localStorage.setItem("worker_tenant_status", "active");
                if (!isCancelled) setIsWizardCompleted(true);
              }

              if ((profile as any).__profile_unavailable) {
                console.warn(
                  "Backend unavailable, preserving setup status if valid draft exists",
                );
                if (!isCancelled) setIsBackendUnavailable(true);
              }

              const { readSafeSetupDraft } = await import("./lib/appSession");
              const safeDraft =
                readSafeSetupDraft(ownerKey || "") ||
                readSafeSetupDraft(tenantId || "") ||
                {};

              if (!isCancelled) {
                setState((prev) =>
                  hydrateSetupStateFromProfile(profile, safeDraft, prev),
                );
              }
            } else {
              const { readSafeSetupDraft } = await import("./lib/appSession");
              const safeDraft =
                readSafeSetupDraft(ownerKey || "") ||
                readSafeSetupDraft(tenantId || "");
              if (safeDraft) {
                normalizedDormName = safeDraft.dormName || "";
                if (!isCancelled)
                  setState((prev) => ({ ...prev, ...safeDraft }));
              }
            }
          } catch (err) {
            const { readSafeSetupDraft } = await import("./lib/appSession");
            const safeDraft =
              readSafeSetupDraft(ownerKey || "") ||
              readSafeSetupDraft(tenantId || "");
            if (safeDraft) {
              normalizedDormName = safeDraft.dormName || "";
              if (!isCancelled) setState((prev) => ({ ...prev, ...safeDraft }));
            }
          } finally {
            if (!isCancelled) setIsLoadingProfile(false);
          }
        } else if (ownerKey) {
          const { readSafeSetupDraft } = await import("./lib/appSession");
          const safeDraft = readSafeSetupDraft(ownerKey || "");
          if (safeDraft) {
            normalizedDormName = safeDraft.dormName || "";
            if (!isCancelled) setState((prev) => ({ ...prev, ...safeDraft }));
          }
        }
      }

      if (isLoggedIn && !normalizedDormName) {
        if (!isCancelled) {
          setIsWizardCompleted(false);
          localStorage.removeItem("worker_tenant_status");
        }
      }

      if (!isCancelled) {
        setBootstrapResolved(true);
        setRouteDecisionReady(true);
      }
    };

    if (!bootstrapResolved) {
      runCanonicalBootstrap();
    }

    return () => {
      isCancelled = true;
    };
  }, [isLoggedIn, bootstrapResolved]);

  const handleCredentialResponse = async (response: any) => {
    // Decode JWT for UI display fallback.
    const base64Url = response.credential.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(""),
    );

    const user = JSON.parse(jsonPayload);

    try {
      // 1. Try real backend auth
      const authRes = await authWithGoogleCredential(response.credential);

      if (
        authRes?.ok === false &&
        (authRes?.code === "api_base_unavailable" ||
          authRes?.code === "config_missing")
      ) {
        console.warn(
          "Auth API unavailable or pending config. Cannot create backend session.",
        );
        alert("ระบบยืนยันตัวตนยังไม่พร้อมใช้งาน กรุณาลองใหม่อีกครั้งภายหลัง");
        return;
      }

      // Write to centralized session storage (no raw Google credential)
      writeAppSession({
        sessionToken: authRes.sessionToken || authRes.session?.token,
        tenantId: authRes.tenantId || null,
        hasTenant: authRes.hasTenant,
        owner: authRes.owner,
      });

      setBootstrapResolved(false);
      setRouteDecisionReady(false);

      // If success, store real state
      setCurrentUser(authRes.owner);
      setIsLoggedIn(true);
      setIsBackendUnavailable(false);
    } catch (err: any) {
      console.error("Backend API auth failed:", err.message);

      const safeBucket = err?.code ? `[${err.code}]` : "[AUTH_ERROR]";
      if (
        isLocalDevHost() &&
        !!localStorage.getItem("local_dev_bridge_active")
      ) {
        console.warn("Falling back to local dev mock session");
        setCurrentUser(user);
        setIsLoggedIn(true);
        localStorage.setItem("mock_google_user", JSON.stringify(user));
        alert("ใช้งานโหมดทดสอบ Local Dev (Mock Mode)");
      } else {
        alert(
          `ไม่สามารถเข้าสู่ระบบได้ กรุณาลองใหม่อีกครั้ง\n\nรหัสข้อผิดพลาด: ${safeBucket}`,
        );
      }
    }
  };

  const handleLogout = () => {
    // 1. Clear known HorPlus auth/session/draft keys safely
    clearAppSession();

    // 2. Clear authenticated UI state
    setIsLoggedIn(false);
    setCurrentUser(null);
    setBootstrapResolved(false);
    setRouteDecisionReady(false);

    // 3. Reset Google button init state
    setIsGoogleScriptLoaded(false);
    setGoogleLoadError(false);

    // 4. Perform hard navigation. Deferring slightly ensures React's SPA route guard
    // does not call history.replaceState() while this is pending, which would cancel it.
    setTimeout(() => {
      window.location.replace("/login");
    }, 50);
  };

  const updateState = (updates: Partial<SetupState>) => {
    setState((prev) => {
      const nextState = { ...prev, ...updates };
      if (currentUser?.email) {
        // Exclude sensitive payment fields and backend-driven provision readback from draft
        const {
          promptPayNumber,
          accountName,
          logoFile,
          mainDormIdPresent,
          publicDormIdPresent,
          provisionStatus,
          apartmentLinked,
          provisionedAtPresent,
          adminPreviewReady,
          tenantPreviewReady,
          publicPreviewLinkAvailable,
          publicPreviewLinkRedacted,
          publicDormId,
          adminPreviewLinkReady,
          tenantPreviewLinkReady,
          tenantPreviewUrlAvailable,
          adminPreviewUrlAvailable,
          previewLinkMode,
          ...draftRest
        } = nextState;
        const safeDraft = { ...draftRest } as Partial<SetupState>;
        // Do not persist blob URLs, they are dead after reload
        if (safeDraft.logoUrl && safeDraft.logoUrl.startsWith("blob:")) {
          delete safeDraft.logoUrl;
        }
        import("./lib/appSession").then(({ writeSafeSetupDraft }) => {
          writeSafeSetupDraft(currentUser?.id || currentUser?.email, safeDraft);
        });
      }
      return nextState;
    });
    // Clear errors for fields as they type
    if (Object.keys(errors).length > 0) {
      setErrors({});
    }
  };

  // Step validation helpers
  const validateStep = (step: number): boolean => {
    const errs: Record<string, string> = {};

    if (step === 1) {
      if (!state.dormName.trim()) {
        errs.dormName = "กรุณากรอกชื่อหอพักเพื่อแสดงความน่าเชื่อถือบนหัวบิล";
      }
      if (!state.province) {
        errs.province = "กรุณาเลือกจังหวัดของที่ตั้งหอพัก";
      }
      if (state.rooms.length === 0) {
        errs.rooms = "กรุณาเพิ่มห้องหรือสร้างตัวอย่างห้องพักอย่างน้อย 1 ห้อง";
      } else {
        const roomNames = state.rooms.map((r) => r.roomName);
        const duplicates = roomNames.filter(
          (item, index) => roomNames.indexOf(item) !== index,
        );
        if (duplicates.length > 0) {
          errs.rooms = `พบเลขห้องซ้ำกันในระบบ (${Array.from(new Set(duplicates)).join(", ")}) กรุณาแก้ไขไม่ให้เลขห้องซ้ำกัน`;
        } else if (
          state.roomInputMethod === "auto" &&
          state.buildingsList.length === 0
        ) {
          errs.rooms =
            'จำเป็นต้องกดปุ่ม "เพิ่มตึกนี้ลงตาราง" เพื่อประทับรับรองแผนผังตึกก่อน จึงจะเข้าขั้นตอนถัดไปได้';
        }
      }
    }

    if (step === 2) {
      if (!state.elecRate || state.elecRate <= 0) {
        errs.elecRate =
          !state.elecRateType || state.elecRateType === "per_unit"
            ? "ค่าไฟต่อหน่วยต้องมากกว่า 0 บาท"
            : "ค่าไฟฟ้าต้องมากกว่า 0 บาท";
      }
      // Water validation - converting to float for checking
      const parsedWater = parseFloat(state.waterRatePrice);
      if (
        isNaN(parsedWater) ||
        parsedWater < 0 ||
        String(state.waterRatePrice).trim() === ""
      ) {
        errs.waterRatePrice =
          "กรุณาระบุมูลค่าค่าน้ำ หรือระบุเป็น 0 หากไม่มีค่าใช้จ่าย";
      }
      if (state.hasCommonFee) {
        const parsedCommon = parseFloat(state.commonFeePrice);
        if (
          isNaN(parsedCommon) ||
          parsedCommon < 0 ||
          String(state.commonFeePrice).trim() === ""
        ) {
          errs.commonFeePrice = "กรุณาระบุค่าส่วนกลาง หรือระบุเป็น 0";
        }
      }
      if (state.hasWifiFee) {
        const parsedWifi = parseFloat(state.wifiFeePrice);
        if (
          isNaN(parsedWifi) ||
          parsedWifi < 0 ||
          String(state.wifiFeePrice).trim() === ""
        ) {
          errs.wifiFeePrice = "กรุณาระบุค่าอินเทอร์เน็ต Wi-Fi หรือระบุเป็น 0";
        }
      }
      if (!state.promptPayNumber) {
        errs.promptPayNumber = "กรุณากรอกเบอร์โทรหรือเลขผู้รับโอนเงินของท่าน";
      } else if (state.promptPayNumber.replace(/\D/g, "").length < 10) {
        errs.promptPayNumber =
          "เบอร์โทรหรือเลขผู้รับโอนเงินต้องประกอบด้วยตัวเลขอย่างน้อย 10 หลัก";
      }
      if (!state.accountName.trim()) {
        errs.accountName =
          "กรุณาระบุชื่อบัญชีผู้รับโอนตามหน้าบัตรเพื่อเตรียมตรวจสลิป";
      } else if (!/^[ก-๙\s.\-()]+/.test(state.accountName.trim())) {
        errs.accountName =
          "กรุณาระบุชื่อบัญชีเป็นภาษาไทยเท่านั้นตามเงื่อนไขความถูกต้องสะกดจริง";
      }
      if (state.meterRolloverLimit <= 0) {
        errs.meterRolloverLimit =
          "มิเตอร์สูงสุดสำหรับหมุนรอบต้องมีค่ามากกว่า 0";
      }
      if (!state.agreeTerms) {
        errs.agreeTerms =
          "กรุณากดเครื่องถูกเพื่อยินยอมยอมรับนิติกรรมและข้อกำหนดความปลอดภัย";
      }
    }

    if (step === 3) {
      if (!state.step3Consent) {
        errs.step3Consent =
          "กรุณายอมรับเงื่อนไขการส่งมอบข้อมูลและตั้งค่าความปลอดภัยก่อนดำเนินการต่อ";
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNextStep = async () => {
    if (validateStep(currentStep)) {
      setIsSubmitting(true);
      try {
        const workerSessionToken = readSessionToken();

        if (currentStep === 1) {
          // No API call on step 1, just local draft
        } else if (currentStep === 2) {
          // No API call on step 2, just local draft
        }

        setCurrentStep((prev) => prev + 1);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleBackStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleFinishedAction = async () => {
    const v1 = validateStep(1);
    const v2 = validateStep(2);
    const v3 = validateStep(3);
    if (v1 && v2 && v3) {
      setIsSubmitting(true);
      try {
        const workerSessionToken = readSessionToken();
        let tenantId = readSessionTenantId();

        if (!workerSessionToken && !isLocalDevHost()) {
          alert(
            "ไม่พบเซสชั่นการเชื่อมต่อ (Session Token) กรุณาล็อกอินใหม่เพื่อดำเนินการบันทึกข้อมูลจริง",
          );
          return;
        }

        if (workerSessionToken) {
          // 1. Create Tenant (Step 1 Data)
          if (!tenantId) {
            if (workerSessionToken.startsWith("local-dev-")) {
              const localTenantId = deriveLocalDevTenantId(
                currentUser?.email || "local",
              );
              const mockRes = mockLocalCreateTenantResponse(
                localTenantId,
                state.dormName,
              );
              tenantId = mockRes.tenant.id;
              writeSessionTenantId(tenantId);
            } else {
              try {
                const { createTenant } = await import("./lib/apiClient");
                const res = await createTenant(workerSessionToken, {
                  dormName: state.dormName,
                  province: state.province,
                  dormType: state.dormType,
                  rooms: state.rooms.map((r, i) => {
                    const matchedBld = state.buildingsList.find(
                      (b) => b.id === r.buildingId,
                    );
                    const bPrefix = matchedBld ? matchedBld.prefix : "";
                    // Infer floorNo from room name (e.g. A201 → floor 2) when not explicitly set
                    let inferredFloor = r.floor || 1;
                    if (!r.floor && r.roomName) {
                      const stripped = bPrefix
                        ? r.roomName.replace(new RegExp("^" + bPrefix, "i"), "")
                        : r.roomName;
                      const match = stripped.match(/^(\d)/); // first digit after prefix = floor
                      if (match) inferredFloor = parseInt(match[1], 10);
                    }
                    let inferredBCode = r.buildingId;
                    if (!inferredBCode && r.roomName) {
                        const m = String(r.roomName).match(/^[A-Za-z]+/);
                        if (m) inferredBCode = m[0].toUpperCase();
                    }
                    inferredBCode = inferredBCode || "MAIN";

                    return {
                      buildingCode: matchedBld
                        ? (matchedBld.prefix?.trim() ? matchedBld.prefix.trim().toUpperCase() : "MAIN")
                        : inferredBCode,
                      floorNo: inferredFloor,
                      roomNo: r.roomName,
                      displayName: r.roomName,
                      sortOrder: i + 1,
                    };
                  }),
                  billingSettings: {
                    electricityRate: state.elecRate,
                    electricityRateType: state.elecRateType || "per_unit",
                    waterRateType: state.waterRateType,
                    waterRateAmount: parseFloat(state.waterRatePrice) || 0,
                    commonFee: state.hasCommonFee
                      ? parseFloat(state.commonFeePrice) || 0
                      : 0,
                    wifiFee: state.hasWifiFee
                      ? parseFloat(state.wifiFeePrice) || 0
                      : 0,
                    meterRoundingRule: state.meterRolloverLimit,
                    promptpayNumber: state.promptPayNumber,
                    receiverName: state.accountName,
                  },
                  themeSettings: {
                    themeId: state.themeId || "neo-saas",
                    primaryColor:
                      themePresets.find((t) => t.id === state.themeId)?.hex ||
                      themePresets[0].hex,
                    logoUrl:
                      state.logoUrl &&
                      state.logoUrl.startsWith("blob:") &&
                      state.logoFile
                        ? undefined
                        : state.logoFile
                          ? state.logoUrl
                          : undefined,
                    logoScale: state.logoScale || 1.0,
                    logoPositionX: state.logoPositionX || 0,
                    logoPositionY: state.logoPositionY || 0,
                  },
                });
                if (res.tenant?.id) {
                  tenantId = res.tenant.id;
                  writeSessionTenantId(tenantId);
                }
              } catch (apiErr: any) {
                if (
                  apiErr.code === "ALREADY_HAS_TENANT" ||
                  apiErr.code === "TENANT_ALREADY_EXISTS"
                ) {
                  alert(
                    "บัญชีนี้มีหอพักที่ลงทะเบียนไว้แล้ว ระบบจะอัปเดตข้อมูลหอพักเดิม",
                  );
                  // Assume we might fetch it or rely on a subsequent step. For now we halt to prevent corruption without tenantId.
                  throw new Error(
                    "Tenant exists but tenantId not in session. Please refresh.",
                  );
                } else {
                  throw apiErr;
                }
              }
            }
          }

          // 2. Update Settings (Step 2 Data)
          if (tenantId) {
            if (workerSessionToken.startsWith("local-dev-")) {
              console.info(
                "[LOCAL DEV BRIDGE] Skipped real updateTenantSettings API.",
              );
            } else {
              let finalLogoUrl = state.logoUrl;
              let logoUploadFailed = false;

              const { updateTenantSettings, uploadTenantLogo } =
                await import("./lib/apiClient");

              if (
                state.logoUrl &&
                state.logoUrl.startsWith("blob:") &&
                state.logoFile
              ) {
                try {
                  const uploadResult = await uploadTenantLogo(
                    workerSessionToken,
                    tenantId,
                    state.logoFile,
                  );
                  if (uploadResult && uploadResult.logoUrl) {
                    finalLogoUrl = uploadResult.logoUrl;
                    setState((prev) => ({
                      ...prev,
                      logoUrl: uploadResult.logoUrl,
                    }));
                  } else {
                    console.error("[logo-upload] failed: no logoUrl returned");
                    logoUploadFailed = true;
                    finalLogoUrl = undefined;
                  }
                } catch (err: any) {
                  console.error("[logo-upload] failed", err);
                  logoUploadFailed = true;
                  finalLogoUrl = undefined;
                }
              } else {
                if (!state.logoFile) {
                  // No logo selected
                  finalLogoUrl = undefined;
                }
              }

              const normalizedWifiFeeType =
                normalizePersonRoomFeeType(state.wifiFeeType) ??
                normalizePersonRoomFeeType(state.wifiRateType) ??
                normalizePersonRoomFeeType((state as any).internetFeeType) ??
                "per_room";

              const normalizedCommonFeeType =
                normalizePersonRoomFeeType(state.commonFeeType) ??
                normalizePersonRoomFeeType((state as any).commonRateType) ??
                "per_room";

              await updateTenantSettings(workerSessionToken, tenantId, {
                billingSettings: {
                  electricityRate: state.elecRate,
                  electricityRateType: state.elecRateType || "per_unit",
                  waterRateType: state.waterRateType,
                  waterRateAmount: parseFloat(state.waterRatePrice) || 0,
                  commonFee: state.hasCommonFee
                    ? parseFloat(state.commonFeePrice) || 0
                    : 0,
                  commonFeeType: normalizedCommonFeeType,
                  wifiFee: state.hasWifiFee
                    ? parseFloat(state.wifiFeePrice) || 0
                    : 0,
                  wifiFeeType: normalizedWifiFeeType,
                  meterRoundingRule: state.meterRolloverLimit,
                  promptpayNumber: state.promptPayNumber,
                  receiverName: state.accountName,
                },
                themeSettings: {
                  themeId: state.themeId,
                  primaryColor:
                    themePresets.find((t) => t.id === state.themeId)?.hex ||
                    themePresets[0].hex,
                  logoUrl: finalLogoUrl,
                  logoScale: state.logoScale,
                  logoPositionX: state.logoPositionX,
                  logoPositionY: state.logoPositionY,
                },
              });

              if (logoUploadFailed) {
                alert("อัปโหลดโลโก้ไม่สำเร็จ แต่สามารถตั้งค่าหอพักต่อได้");
              }
            }

            // 3. Complete Onboarding
            if (workerSessionToken.startsWith("local-dev-")) {
              const mockComplete =
                mockLocalCompleteOnboardingResponse(tenantId);
              writeSessionTenantActive();
              localStorage.removeItem("local_dev_bridge_active");
            } else {
              const { completeTenantOnboarding } =
                await import("./lib/apiClient");
              await completeTenantOnboarding(workerSessionToken, tenantId);
              writeSessionTenantActive();
              setState((prev) => ({
                ...prev,
                apartmentLinked: true,
                provisionStatus: "active",
              }));
            }
          }
        }

        // Final success state
        setShowSuccessPopup(true);
      } catch (err: any) {
        console.error("Setup Completion Error:", err);
        alert(
          "ไม่สามารถดำเนินการจนเสร็จสิ้นได้: " +
            (err.message || "ข้อผิดพลาดระบบ"),
        );
      } finally {
        setIsSubmitting(false);
      }
    } else {
      alert("พบข้อมูลกรอกผิดพลาดในขั้นตอนก่อนหน้า กรุณากดตรวจสอบอีกครั้ง");
    }
  };

  const handleResetSetupWizard = () => {
    setState(initialSetupState);
    setCurrentStep(1);
    setIsWizardCompleted(false);
    setShowSuccessPopup(false);
    setErrors({});
    if (currentUser?.id || currentUser?.email) {
      import("./lib/appSession").then(({ clearOwnerDraft }) => {
        clearOwnerDraft(currentUser?.id || currentUser?.email);
      });
    }
  };

  const handleSuccessPopupCompleted = () => {
    setShowSuccessPopup(false);
    setIsWizardCompleted(true);
  };

  if (!isLoggedIn) {
    const normPath =
      currentPath.endsWith("/") && currentPath.length > 1
        ? currentPath.slice(0, -1)
        : currentPath;
    if (normPath !== "/login" && normPath !== "/login.html") return null; // Wait for redirect to complete
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    const searchParams = new URLSearchParams(window.location.search);
    const isMockEntry =
      searchParams.get("e2eLocal") === "1" && searchParams.get("mock") === "1";

    if (isMockEntry) {
      const buildMockPayload = (isOwnerB: boolean) => {
        const ownerKey = isOwnerB ? "MOCK_B" : "MOCK_A";
        const dormId = isOwnerB ? "MOCK_DORM_B" : "MOCK_DORM_A";
        const roomId = isOwnerB ? "B202" : "A101";
        const tenantId = isOwnerB ? "MOCK_TENANT_B" : "MOCK_TENANT_A";
        const payload = {
          schemaVersion: "owner-scoped-e2e-v1",
          source: "setup-wizard",
          e2eLocal: true,
          mock: true,
          ownerKey,
          dormId,
          dormName: isOwnerB ? "Mock Dorm B" : "Mock Dorm A",
          roomId,
          roomName: roomId,
          tenantId,
          billId: isOwnerB ? "MOCK_BILL_B" : "MOCK_BILL_A",
          timestamp: new Date().toISOString(),
        };
        return "&payload=" + encodeURIComponent(btoa(JSON.stringify(payload)));
      };

      return (
        <div className="min-h-screen flex items-center justify-center bg-[linear-gradient(135deg,_#fdfbf7_0%,_#f5f0e6_100%)] font-sans p-4">
          <div className="bg-white p-8 rounded-[32px] shadow-xl max-w-md w-full text-center border border-indigo-200">
            <h1 className="text-2xl font-extrabold text-indigo-900 mb-4">
              Internal Mock Test Panel
            </h1>
            <p className="text-sm text-slate-500 mb-6">
              Bypassing Google Login for manual E2E preview test.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() =>
                  window.open(
                    (import.meta.env.VITE_APARTMENT_ADMIN_BASE_URL || "") +
                      "?e2eLocal=1&mock=1&handoff=wizard" +
                      buildMockPayload(false),
                    "_blank",
                  )
                }
                className="px-4 py-3 bg-indigo-50 border border-indigo-200 text-indigo-700 font-bold rounded-xl hover:bg-indigo-100 transition"
              >
                Open Admin Preview Owner A
              </button>
              <button
                onClick={() =>
                  window.open(
                    (import.meta.env.VITE_APARTMENT_ADMIN_BASE_URL || "") +
                      "?e2eLocal=1&mock=1&handoff=wizard" +
                      buildMockPayload(true),
                    "_blank",
                  )
                }
                className="px-4 py-3 bg-indigo-50 border border-indigo-200 text-indigo-700 font-bold rounded-xl hover:bg-indigo-100 transition"
              >
                Open Admin Preview Owner B
              </button>
              <button
                onClick={() => {
                  const mockUser = {
                    id: "mockA-id",
                    email: "mockA@example.com",
                    displayName: "Mock Owner A",
                    name: "Mock Owner A",
                  };
                  setCurrentUser(mockUser);
                  setIsLoggedIn(true);
                  localStorage.setItem(
                    "mock_google_user",
                    JSON.stringify(mockUser),
                  );
                  writeAppSession({
                    sessionToken: "local-dev-token-A",
                    tenantId: "MOCK_DORM_A",
                    hasTenant: false,
                    owner: mockUser,
                  });
                }}
                className="px-4 py-3 bg-emerald-50 border border-emerald-200 text-emerald-700 font-bold rounded-xl hover:bg-emerald-100 transition mt-4"
              >
                Start Owner A Preview (Wizard)
              </button>
              <button
                onClick={() => {
                  const mockUser = {
                    id: "mockB-id",
                    email: "mockB@example.com",
                    displayName: "Mock Owner B",
                    name: "Mock Owner B",
                  };
                  setCurrentUser(mockUser);
                  setIsLoggedIn(true);
                  localStorage.setItem(
                    "mock_google_user",
                    JSON.stringify(mockUser),
                  );
                  writeAppSession({
                    sessionToken: "local-dev-token-B",
                    tenantId: "MOCK_DORM_B",
                    hasTenant: false,
                    owner: mockUser,
                  });
                }}
                className="px-4 py-3 bg-emerald-50 border border-emerald-200 text-emerald-700 font-bold rounded-xl hover:bg-emerald-100 transition"
              >
                Start Owner B Preview (Wizard)
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-[linear-gradient(135deg,_#fdfbf7_0%,_#f5f0e6_100%)] font-sans p-4">
        <div className="bg-white p-8 rounded-[32px] shadow-xl max-w-md w-full text-center border border-slate-100">
          <div className="mx-auto w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mb-6">
            <Building2 className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="text-2xl font-extrabold text-amber-950 font-serif mb-2">
            เข้าสู่ระบบเจ้าของหอพัก
          </h1>
          <p className="text-slate-500 text-sm mb-8">
            ใช้บัญชี Google เพื่อเริ่มลงทะเบียนและตั้งค่าหอพักของคุณ
          </p>

          <div className="flex flex-col items-center justify-center mb-6 min-h-[44px]">
            <div
              id="google-signin-button"
              className="min-h-[44px] w-full flex justify-center"
            ></div>
            {!isGoogleScriptLoaded && !googleLoadError && (
              <div className="flex items-center gap-2 text-slate-500 text-sm animate-pulse">
                <div className="w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                กำลังโหลดปุ่มเข้าสู่ระบบ...
              </div>
            )}
            {googleLoadError && (
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-rose-50 text-rose-600 rounded-xl text-sm font-bold border border-rose-200 hover:bg-rose-100 transition"
              >
                โหลดใหม่ (ไม่สามารถเชื่อมต่อ Google ได้)
              </button>
            )}
          </div>

          {!clientId && (
            <div className="mt-4 p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-700 text-left flex items-start gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                <strong>
                  ยังไม่ได้ตั้งค่า Google Client ID ใน environment
                </strong>
                <p className="mt-1 opacity-90">
                  Developer: กรุณาเพิ่ม VITE_GOOGLE_CLIENT_ID ในไฟล์ .env
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  const renderIsSetupComplete =
    isWizardCompleted ||
    localStorage.getItem("worker_tenant_status") === "active" ||
    sessionStorage.getItem("wiz_has_tenant") === "true" ||
    (isLoggedIn && state.dormName && state.dormName !== "ข้อมูลหอพักของคุณ");
  if (
    isBackendUnavailable &&
    !renderIsSetupComplete &&
    currentPath.includes("/setup")
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[linear-gradient(135deg,_#fdfbf7_0%,_#f5f0e6_100%)] font-sans p-4">
        <div className="bg-white p-8 rounded-[32px] shadow-xl text-center border border-slate-100 flex flex-col items-center gap-4 max-w-sm w-full">
          <div className="text-4xl">โš ๏ธ </div>
          <h2 className="text-lg font-bold text-slate-800 mt-2">
            ไม่สามารถตรวจสอบข้อมูลหอพักได้
          </h2>
          <p className="text-xs text-slate-500">
            กรุณาลองรีเฟรชหน้าเว็บอีกครั้ง
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 w-full h-12 rounded-xl font-bold bg-amber-500 text-white hover:bg-amber-600 transition-colors"
          >
            ลองใหม่
          </button>
        </div>
      </div>
    );
  }

  if (isLoadingProfile || !routeDecisionReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[linear-gradient(135deg,_#fdfbf7_0%,_#f5f0e6_100%)] font-sans p-4">
        <div className="bg-white p-8 rounded-[32px] shadow-xl text-center border border-slate-100 flex flex-col items-center gap-4 max-w-sm w-full">
          <div className="w-12 h-12 border-4 border-amber-100 border-t-amber-600 rounded-full animate-spin" />
          <h2 className="text-lg font-bold text-slate-800 mt-2">
            กำลังโหลดข้อมูลหอพักเดิม...
          </h2>
          <p className="text-xs text-slate-500">
            กรุณารอสักครู่ ระบบกำลังเรียกคืนการตั้งค่าของคุณ
          </p>
        </div>
      </div>
    );
  }

  const normPath =
    currentPath.endsWith("/") && currentPath.length > 1
      ? currentPath.slice(0, -1)
      : currentPath;
  const isAllowedAuthRoute = [
    "/setup",
    "/setup.html",
    "/dashboard",
    "/dashboard.html",
  ].includes(normPath);

  if (isLoggedIn && !isAllowedAuthRoute) {
    return null; // Prevent rendering partial authenticated shell on unknown paths
  }

  return (
    <div className="min-h-screen py-6 px-4 sm:px-6 lg:px-8 font-sans bg-orange-50/20 text-stone-900 bg-[linear-gradient(135deg,_#fdfbf7_0%,_#f5f0e6_100%)] relative">
      <div className="absolute top-4 right-4 z-50 flex flex-col items-end gap-1.5">
        {/* LOCAL DEV BRIDGE: Subtle indicator โ€” only shown on localhost/dev */}
        {isLocalDevHost() &&
          !!localStorage.getItem("local_dev_bridge_active") && (
            <div className="px-2 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-[10px] font-bold text-amber-800 select-none">
              โหมดทดสอบ Local Dev
            </div>
          )}
        <button
          onClick={handleLogout}
          className="text-xs text-slate-500 hover:text-slate-800 underline transition"
        >
          ออกจากระบบ
        </button>
      </div>

      {currentPath === "/dashboard" || currentPath === "/dashboard.html" ? (
        /* Embed variables and configurations directly in the core Webpage Dashboard layout view */
        <DormDashboard
          state={state}
          onReset={handleResetSetupWizard}
          onChange={updateState}
        />
      ) : currentPath === "/setup" || currentPath === "/setup.html" ? (
        /* Main Wizard Frame */
        <main className="max-w-7xl mx-auto">
          {/* Banner with logo info */}
          <div className="text-center mb-8 max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 border border-amber-105 text-amber-800 font-bold text-[10px] uppercase tracking-wider mb-3">
              <Building2 className="w-3.5 h-3.5" />
              <span>ระบบสมาชิกลงทะเบียนนเจ้าของหอพัก</span>
            </div>

            <h1 className="text-2xl sm:text-4xl font-extrabold tracking-tight text-amber-950 font-serif">
              ระบบช่วยเปิดบริการหอพักเริ่มต้น
            </h1>
            <p className="mt-2 text-xs sm:text-sm text-stone-600 font-sans leading-relaxed">
              ยังไม่มีหอพักในบัญชีนี้ เริ่มตั้งค่าได้เลย
              เพื่อให้ผู้เช่าสามารถประทับใจ เข้าถึงบิล ดูรายละเอียดจอง
              และชำระเงินผ่านระบบ QR Code อัจฉริยะได้ทันที
            </p>
          </div>

          {/* STEPPER PROGRESS BAR */}
          <div className="max-w-3xl mx-auto mb-10">
            <div className="flex items-center justify-between relative z-10">
              {/* Stepper link indicators line with nested inner active track */}
              <div className="absolute left-[18px] right-[18px] sm:left-[22px] sm:right-[22px] top-[18px] sm:top-[22px] -translate-y-1/2 h-[3.5px] bg-slate-200 z-0 rounded-full overflow-hidden">
                <div
                  className="h-full bg-amber-600 transition-all duration-500 rounded-full"
                  style={{
                    width:
                      currentStep === 1
                        ? "0%"
                        : currentStep === 2
                          ? "50%"
                          : "100%",
                  }}
                />
              </div>

              {[
                { num: 1, title: "ข้อมูลหอพัก", desc: "ระบุตึกและรายห้อง" },
                {
                  num: 2,
                  title: "ตั้งค่าบิลหอพัก",
                  desc: "เลือกธีมและอัตราราคา",
                },
                { num: 3, title: "ตรวจสอบ", desc: "ข้อมูลความปลอดภัย" },
              ].map((st) => {
                const isActive = currentStep === st.num;
                const isCompleted = currentStep > st.num;
                return (
                  <div
                    key={st.num}
                    className="flex flex-col items-center relative z-10"
                  >
                    <div
                      className={`w-9 h-9 sm:w-11 sm:h-11 rounded-full flex items-center justify-center font-bold text-sm sm:text-base border-2 transition-all duration-300 ${
                        isActive
                          ? "bg-amber-600 text-white border-amber-600 scale-110 shadow-lg shadow-amber-100 ring-4 ring-amber-50"
                          : isCompleted
                            ? "bg-amber-500 text-white border-amber-500"
                            : "bg-white text-slate-400 border-slate-200"
                      }`}
                    >
                      {isCompleted ? <Check className="w-5 h-5" /> : st.num}
                    </div>
                    <span className="text-[11px] font-extrabold text-slate-700 mt-2 font-sans">
                      {st.title}
                    </span>
                    <span className="hidden sm:inline text-[9px] text-slate-400 mt-0.5 font-sans font-medium">
                      {st.desc}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* COMPILATION VIEWPORT GRID LAYOUT */}
          {/* Left side is Form Fields, Right side is Sticky Bill Preview */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* LEFT AREA: FORMS PANEL */}
            <div className="lg:col-span-7 bg-white border border-slate-150 rounded-[32px] p-5 sm:p-8 shadow-xl/3 flex flex-col justify-between min-h-[500px]">
              {/* Dynamic STEP renderings */}
              <div className="flex-1">
                {currentStep === 1 && (
                  <div className="animate-fade-in">
                    <Step1Form
                      state={state}
                      onChange={updateState}
                      errors={errors}
                    />
                  </div>
                )}

                {currentStep === 2 && (
                  <div className="animate-fade-in">
                    <Step2Form
                      state={state}
                      onChange={updateState}
                      errors={errors}
                    />
                  </div>
                )}

                {currentStep === 3 && (
                  <div className="animate-fade-in">
                    <SummaryStep
                      state={state}
                      currentUser={currentUser}
                      onChange={updateState}
                      errors={errors}
                    />
                  </div>
                )}
              </div>

              {/* ERROR DISPLAY AT PANEL FOOTER */}
              {Object.keys(errors).length > 0 && (
                <div className="mt-6 bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-2xl flex items-start gap-2.5 text-xs">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div className="space-y-0.5">
                    <span className="font-bold">
                      กรุณาตรวจสอบและดำเนินการแก้ไขจุดบกพร่อง:
                    </span>
                    <ul className="list-disc list-inside space-y-0.5 pl-1">
                      {Object.values(errors).map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* BUTTON BAR FOOTERS */}
              <div className="border-t border-slate-100 pt-6 mt-8 flex items-center justify-between font-sans gap-3">
                {/* Back Button */}
                <div>
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={handleBackStep}
                      className="px-5 py-3 rounded-2xl border border-slate-200 hover:border-slate-350 bg-white text-slate-700 hover:text-slate-900 text-sm font-bold flex items-center gap-1.5 transition duration-150 cursor-pointer"
                    >
                      <ChevronLeft className="w-4.5 h-4.5 text-slate-500" />
                      <span>กลับขั้นตอนก่อนหน้า</span>
                    </button>
                  )}
                </div>

                {/* Next / Finished Button */}
                <div>
                  {currentStep < 3 ? (
                    <button
                      type="button"
                      data-testid="wizard-next-btn"
                      onClick={handleNextStep}
                      disabled={isSubmitting}
                      className={`px-6 py-3 rounded-2xl ${isSubmitting ? "bg-amber-400" : "bg-amber-600 hover:bg-amber-700 active:bg-amber-800"} text-white text-sm font-bold flex items-center gap-1.5 shadow-md shadow-amber-100 transition duration-150 cursor-pointer disabled:cursor-not-allowed`}
                    >
                      <span>
                        {isSubmitting ? "กำลังบันทึกข้อมูล..." : "ขั้นตอนถัดไป"}
                      </span>
                      {!isSubmitting && (
                        <ChevronRight className="w-4.5 h-4.5 text-white/90" />
                      )}
                    </button>
                  ) : (
                    <button
                      type="button"
                      data-testid="wizard-finish-btn"
                      onClick={handleFinishedAction}
                      disabled={isSubmitting}
                      className="px-8 py-3 rounded-2xl bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800 text-sm font-bold flex items-center gap-1.5 shadow-lg shadow-emerald-150 select-none transition-all duration-300 transform active:scale-95 cursor-pointer disabled:opacity-50"
                    >
                      <CheckCircle2 className="w-5 h-5 text-white" />
                      <span>บันทึกและตั้งค่าสำเร็จ (Finished)</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* RIGHT AREA: STICKY BILL LIVE PREVIEW */}
            <div className="lg:col-span-5 lg:sticky lg:top-8 animate-fade-in">
              <div className="bg-slate-100/50 rounded-[32px] p-4 border border-slate-200/50">
                <BillPreview state={state} />
              </div>
            </div>
          </div>
        </main>
      ) : null}

      {/* POPUP OVERLAY ON SUCCESS */}
      {showSuccessPopup && (
        <SuccessPopup onComplete={handleSuccessPopupCompleted} state={state} />
      )}

      {/* Admin / Developer Contact Footer */}
      <footer className="mt-16 text-center font-sans tracking-wide pb-12 select-none">
        <a
          href="https://linktr.ee/phoomwork"
          target="_blank"
          rel="noopener noreferrer"
          className="relative block w-full max-w-[520px] mx-auto px-4 py-3.5 text-center cursor-pointer bg-gradient-to-b from-white to-slate-50 text-slate-500 border border-slate-200/60 rounded-2xl shadow-sm hover:shadow-md hover:border-[#22c55e]/30 transition-all duration-300 select-none group focus:outline-none focus:ring-4 focus:ring-emerald-500/10"
          role="button"
        >
          <div className="absolute left-1/2 top-0 w-[38%] h-[2px] -translate-x-1/2 rounded-full bg-gradient-to-r from-transparent via-[#22c55e] to-transparent opacity-75" />

          <div className="inline-flex items-center justify-center gap-1.5 text-[12.5px] font-black text-slate-700 tracking-wide mb-1 select-none">
            <div className="relative flex items-center justify-center w-1.5 h-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#22c55e] shadow-[0_0_0_4px_rgba(34,197,94,0.13)] animate-ping absolute" />
              <span className="w-1.5 h-1.5 rounded-full bg-[#22c55e] shadow-[0_0_0_4px_rgba(34,197,94,0.13)] relative" />
            </div>
            ติดต่อ / สอบถาม / แจ้งปัญหา
          </div>

          <div className="text-[11.7px] leading-relaxed text-slate-550 font-bold mb-1">
            Facebook : Phoom Tanalad · LINE : phoomphoom007
          </div>

          <div className="w-fit mx-auto mt-1.5 text-[11.4px] font-bold text-[#16a34a] border-b border-[#16a34a]/35 pb-0.5 group-hover:text-emerald-700 transition duration-150">
            แตะเพื่อเปิดช่องทางติดต่อ ↗
          </div>
        </a>
      </footer>
    </div>
  );
}

const normalizePersonRoomFeeType = (value: unknown): "per_room" | "per_person" | undefined => {
  return value === "per_person" || value === "per_room" ? value : undefined;
};

export function hydrateSetupStateFromProfile(
  profile: any,
  safeDraft: any,
  prev: any,
) {
  let reconstructedBuildings: any[] =
    safeDraft.buildingsList || prev.buildingsList || [];
  if (profile.rooms && profile.rooms.length > 0) {
    const bMap = new Map<
      string,
      {
        id: string;
        prefix: string;
        floorNos: Set<number>;
        totalRooms: number;
        format: string;
      }
    >();
    for (const r of profile.rooms) {
      let bId = r.buildingCode || "A";
      // Normalize timestamp-like IDs to human-readable prefix
      if (/^\d{12,14}$/.test(bId)) {
        const src = r.displayName || r.roomNo || "";
        const match = src.match(/^([a-zA-Z]+)/);
        bId = match ? match[1].toUpperCase() : "A";
      }

      // Infer floor: prefer explicit floorNo, but if all are 1 and roomNo encodes a floor digit, use that
      let floor = r.floorNo || 1;
      if (floor === 1) {
        const src = r.displayName || r.roomNo || "";
        const stripped = src.replace(new RegExp("^" + bId, "i"), ""); // strip building prefix
        const m = stripped.match(/^(\d)/);
        if (m) {
          const inferred = parseInt(m[1], 10);
          if (inferred > 0) floor = inferred;
        }
      }

      if (!bMap.has(bId)) {
        bMap.set(bId, {
          id: bId,
          prefix: bId,
          floorNos: new Set([floor]),
          totalRooms: 1,
          format: "prefix-floor-room",
        });
      } else {
        const b = bMap.get(bId)!;
        b.floorNos.add(floor);
        b.totalRooms++;
      }
    }
    reconstructedBuildings = Array.from(bMap.values()).map((b) => {
      const floorCount = b.floorNos.size;
      const totalRooms = b.totalRooms;
      const roomsPerFloor =
        floorCount > 0 ? Math.round(totalRooms / floorCount) : totalRooms;
      return {
        id: b.id,
        prefix: b.prefix,
        floors: floorCount,
        roomsPerFloor,
        totalRooms,
        format: b.format,
      };
    });
  }

  const isStaleDraft = !profile.billingSettings || !profile.themeSettings;
  const normalizedDormName =
    profile.tenant?.dormName && profile.tenant.dormName !== "ข้อมูลหอพักของคุณ"
      ? profile.tenant.dormName
      : safeDraft.dormName || "";

  return {
    ...prev,
    ...safeDraft,
    tenantStatus: profile.tenant?.status,
    isViewingStaleDraft: isStaleDraft,
    buildingsList: reconstructedBuildings,
    ownerName:
      profile.tenant?.ownerName || safeDraft.ownerName || prev.ownerName || "",
    dormName: normalizedDormName || prev.dormName,
    province: profile.tenant?.province || safeDraft.province || prev.province,
    dormType: profile.tenant?.dormType || safeDraft.dormType || prev.dormType,
    roomInputMethod: "manual",
    rooms:
      profile.rooms && profile.rooms.length > 0
        ? profile.rooms.map((r: any) => ({
            roomName: r.displayName || r.roomNo,
            buildingId: r.buildingCode,
            floor: r.floorNo,
          }))
        : safeDraft.rooms || prev.rooms,
    ...(profile.billingSettings
      ? {
          elecRate:
            profile.billingSettings.electricityRate ??
            safeDraft.elecRate ??
            prev.elecRate,
          elecRateType:
            profile.billingSettings.electricityRateType ??
            safeDraft.elecRateType ??
            prev.elecRateType ??
            "per_unit",
          waterRateType:
            profile.billingSettings.waterRateType ??
            safeDraft.waterRateType ??
            prev.waterRateType,
          waterRatePrice:
            profile.billingSettings.waterRateAmount !== undefined &&
            profile.billingSettings.waterRateAmount !== null
              ? String(profile.billingSettings.waterRateAmount)
              : (safeDraft.waterRatePrice ?? prev.waterRatePrice),
          hasCommonFee:
            (profile.billingSettings.commonFee || 0) > 0 ||
            !!safeDraft.hasCommonFee,
          commonFeePrice:
            profile.billingSettings.commonFee !== undefined &&
            profile.billingSettings.commonFee !== null
              ? String(profile.billingSettings.commonFee)
              : (safeDraft.commonFeePrice ?? prev.commonFeePrice),
          commonFeeType:
            normalizePersonRoomFeeType(profile.billingSettings.commonFeeType) ??
            normalizePersonRoomFeeType(profile.billingSettings.commonRateType) ??
            "per_room",
          hasWifiFee:
            (profile.billingSettings.wifiFee || 0) > 0 ||
            !!safeDraft.hasWifiFee,
          wifiFeePrice:
            profile.billingSettings.wifiFee !== undefined &&
            profile.billingSettings.wifiFee !== null
              ? String(profile.billingSettings.wifiFee)
              : (safeDraft.wifiFeePrice ?? prev.wifiFeePrice),
          wifiFeeType:
            normalizePersonRoomFeeType(profile.billingSettings.wifiFeeType) ??
            normalizePersonRoomFeeType(profile.billingSettings.wifiRateType) ??
            normalizePersonRoomFeeType(profile.billingSettings.internetFeeType) ??
            "per_room",
          meterRolloverLimit:
            profile.billingSettings.meterRoundingRule ??
            safeDraft.meterRolloverLimit ??
            prev.meterRolloverLimit,
          promptPayNumber:
            profile.billingSettings.promptpayNumber ||
            safeDraft.promptPayNumber ||
            prev.promptPayNumber,
          accountName:
            profile.billingSettings.receiverName ||
            safeDraft.accountName ||
            prev.accountName,
        }
      : {}),
    ...(profile.themeSettings
      ? {
          themeId:
            profile.themeSettings.themeId || safeDraft.themeId || prev.themeId,
          logoUrl:
            profile.themeSettings.logoUrl || safeDraft.logoUrl || prev.logoUrl,
          logoScale:
            profile.themeSettings.logoScale ||
            safeDraft.logoScale ||
            prev.logoScale,
          logoPositionX:
            profile.themeSettings.logoPositionX ??
            safeDraft.logoPositionX ??
            prev.logoPositionX ??
            0,
          logoPositionY:
            profile.themeSettings.logoPositionY ??
            safeDraft.logoPositionY ??
            prev.logoPositionY ??
            0,
        }
      : {}),
    mainDormIdPresent:
      profile.tenant?.mainDormIdPresent ?? prev.mainDormIdPresent,
    publicDormIdPresent:
      profile.tenant?.publicDormIdPresent ?? prev.publicDormIdPresent,
    provisionStatus: profile.tenant?.provisionStatus ?? prev.provisionStatus,
    apartmentLinked: profile.tenant?.apartmentLinked ?? prev.apartmentLinked,
    provisionedAtPresent:
      profile.tenant?.provisionedAtPresent ?? prev.provisionedAtPresent,
  };
}
